function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useMemo, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import Log from '@deephaven/log';
import { useForwardedRef } from '@deephaven/react-hooks';
import { DEFAULT_GET_PREFERRED_REPLACEMENT_STRING, fillToLength, trimTrailingMask } from "./MaskedInputUtils.js";
import "./MaskedInput.css";
import { jsx as _jsx } from "react/jsx-runtime";
var log = Log.module('MaskedInput');
var SELECTION_DIRECTION = {
  FORWARD: 'forward',
  BACKWARD: 'backward',
  NONE: 'none'
};

/**
 * Special space character that's the same size as tabular numbers
 * https://www.fileformat.info/info/unicode/char/2007/index.htm
 */
var FIXED_WIDTH_SPACE = '\u2007';
/**
 * A masked input for entering data from a template.
 * Won't work by itself, must use within another component and handle updating the value/selection.
 */
// Forward ref causes a false positive for display-name in eslint:
// https://github.com/yannickcr/eslint-plugin-react/issues/2269
// eslint-disable-next-line react/display-name
var MaskedInput = /*#__PURE__*/React.forwardRef((props, ref) => {
  var className = props.className,
    example = props.example,
    _props$getNextSegment = props.getNextSegmentValue,
    getNextSegmentValue = _props$getNextSegment === void 0 ? (range, delta, segmentValue) => segmentValue : _props$getNextSegment,
    _props$getPreferredRe = props.getPreferredReplacementString,
    getPreferredReplacementString = _props$getPreferredRe === void 0 ? DEFAULT_GET_PREFERRED_REPLACEMENT_STRING : _props$getPreferredRe,
    _props$normalizePaste = props.normalizePastedText,
    normalizePastedText = _props$normalizePaste === void 0 ? text => text : _props$normalizePaste,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? () => false : _props$onChange,
    _props$onSelect = props.onSelect,
    onSelect = _props$onSelect === void 0 ? () => false : _props$onSelect,
    onSubmit = props.onSubmit,
    pattern = props.pattern,
    placeholder = props.placeholder,
    selection = props.selection,
    value = props.value,
    _props$onFocus = props.onFocus,
    onFocus = _props$onFocus === void 0 ? () => false : _props$onFocus,
    _props$onBlur = props.onBlur,
    onBlur = _props$onBlur === void 0 ? () => false : _props$onBlur,
    dataTestId = props['data-testid'];
  var input = useForwardedRef(ref);
  var examples = useMemo(() => Array.isArray(example) ? example : [example], [example]);
  var emptyMask = useMemo(() => examples[0].replace(/[a-zA-Z0-9]/g, FIXED_WIDTH_SPACE), [examples]);
  useEffect(function setSelectedSegment() {
    if (selection != null) {
      var _input$current;
      log.debug('setting selection...', selection);
      var selectionStart = selection.selectionStart,
        selectionEnd = selection.selectionEnd,
        selectionDirection = selection.selectionDirection;
      (_input$current = input.current) === null || _input$current === void 0 || _input$current.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
      log.debug('selection set!');
    }
  }, [selection, input]);

  /**
   * Returns the selection range for the segment at the given cursor position
   * @param cursorPosition The current position of the cursor
   */
  var getSegment = useCallback(cursorPosition => {
    var selectionStart = cursorPosition;
    var selectionEnd = cursorPosition;
    var testValue = examples.length > 0 ? examples[0] : value;
    for (var i = selectionStart - 1; i >= 0; i -= 1) {
      if (!/[a-zA-Z0-9]/g.test(testValue.charAt(i))) {
        break;
      }
      selectionStart = i;
    }
    for (var _i = selectionEnd; _i < testValue.length; _i += 1) {
      if (!/[a-zA-Z0-9]/g.test(testValue.charAt(_i))) {
        break;
      }
      selectionEnd = _i + 1;
    }
    var selectionDirection = selectionStart === selectionEnd ? SELECTION_DIRECTION.NONE : SELECTION_DIRECTION.BACKWARD;
    return {
      selectionStart,
      selectionEnd,
      selectionDirection
    };
  }, [examples, value]);

  /**
   * Replaces all blank spaces and everything after the current cursor position with the example value
   * @param checkValue The value to check/fill in
   * @param exampleValue The example to fill in the value from
   * @param cursorPosition The cursor position
   * @returns The filled in value
   */
  function fillValue(checkValue, exampleValue) {
    var cursorPosition = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : checkValue.length;
    var filledValue = '';
    for (var i = 0; i < cursorPosition; i += 1) {
      if (checkValue.charAt(i) !== FIXED_WIDTH_SPACE) {
        filledValue = filledValue.concat(checkValue[i]);
      } else {
        filledValue = filledValue.concat(exampleValue[i]);
      }
    }
    filledValue = filledValue.concat(exampleValue.substring(cursorPosition));
    return filledValue;
  }

  /**
   * Checks if a given `value` is valid up until the `cursorPosition`.
   * Uses the examples to build the rest of the string
   * @param checkValue The value to check validity of
   * @param cursorPosition The position of the cursor to check up to
   */
  var isValid = useCallback(function (checkValue) {
    var cursorPosition = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : checkValue.length;
    var patternRegex = new RegExp("^".concat(pattern, "$"));
    if (patternRegex.test(checkValue)) {
      return true;
    }
    for (var i = 0; i < examples.length; i += 1) {
      var filledValue = fillValue(checkValue, examples[i], cursorPosition);
      if (patternRegex.test(filledValue)) {
        return true;
      }
    }
    return false;
  }, [pattern, examples]);

  /**
   * Returns the next segment after the given position
   * @param position The cursor position to start at
   * @returns The new selection range
   */
  function nextSegment(position) {
    var currentSegment = getSegment(position);
    var nextPosition = currentSegment.selectionEnd + 1;
    if (nextPosition >= value.length) {
      return currentSegment;
    }
    return getSegment(nextPosition);
  }

  /**
   * Returns the previous segment before the given position
   * @param position The cursor position to start at
   * @returns The new selection range
   */
  function previousSegment(position) {
    var currentSegment = getSegment(position);
    var previousPosition = currentSegment.selectionStart - 1;
    if (previousPosition <= 0) {
      return currentSegment;
    }
    return getSegment(previousPosition);
  }
  function nextSegmentValue(position, delta) {
    var segment = getSegment(position);
    var segmentValue = value.substring(segment.selectionStart, segment.selectionEnd);
    var newSegmentValue = getNextSegmentValue(segment, delta, segmentValue, value);
    var newValue = value.substring(0, segment.selectionStart) + newSegmentValue + value.substring(segment.selectionEnd);
    if (isValid(newValue, segment.selectionEnd)) {
      onChange(newValue);
      onSelect(segment);
    }
  }
  var handleSelect = useCallback(event => {
    var _ref = event.target,
      _ref$selectionStart = _ref.selectionStart,
      selectionStart = _ref$selectionStart === void 0 ? 0 : _ref$selectionStart,
      _ref$selectionEnd = _ref.selectionEnd,
      selectionEnd = _ref$selectionEnd === void 0 ? 0 : _ref$selectionEnd,
      _ref$selectionDirecti = _ref.selectionDirection,
      selectionDirection = _ref$selectionDirecti === void 0 ? 'none' : _ref$selectionDirecti;
    if (selectionStart === null || selectionEnd === null || selectionDirection === null) {
      log.error('Selection attempted on non-text input element', event.target);
      return;
    }
    log.debug2('handleSelect', selectionStart, selectionEnd, selectionDirection);
    if (selection != null && selectionStart === selection.selectionStart && selectionEnd === selection.selectionEnd) {
      return;
    }
    if (selection != null && selectionStart === value.length && selectionEnd === value.length && event.nativeEvent.type !== 'mouseup') {
      // React triggers onSelect event with the cursor at the end of the input content
      // when the component is rendered at a different location in the DOM,
      // i.e. when start/end times switch places in the TimeSlider.
      // Ignore this event and reset the selection to its previous state.
      onSelect(_objectSpread({}, selection));
      return;
    }
    if (selectionStart === selectionEnd) {
      var newSelection = getSegment(selectionStart);
      log.debug('Selection segment from ', selectionStart, selectionEnd, '=>', newSelection);
      onSelect(newSelection);
    } else {
      onSelect({
        selectionStart,
        selectionEnd,
        selectionDirection
      });
    }
  }, [getSegment, onSelect, selection, value]);
  var handleSelectCapture = useCallback(event => {
    var _input$current$select;
    if (!input.current) {
      return;
    }
    log.debug('handleSelectCapture', event);
    var selectionStart = (_input$current$select = input.current.selectionStart) !== null && _input$current$select !== void 0 ? _input$current$select : 0;
    if (selectionStart === value.length && selection != null && selectionStart !== selection.selectionStart) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [input, selection, value]);
  function handleArrowKey(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!input.current) {
      return;
    }
    var key = event.key;
    var _input$current2 = input.current,
      _input$current2$selec = _input$current2.selectionStart,
      selectionStart = _input$current2$selec === void 0 ? 0 : _input$current2$selec,
      _input$current2$selec2 = _input$current2.selectionEnd,
      selectionEnd = _input$current2$selec2 === void 0 ? 0 : _input$current2$selec2;
    if (selectionStart === null || selectionEnd === null) {
      log.error('Selection arrow nvaigation attempted on non-text input element', event.target);
      return;
    }
    if (key === 'ArrowLeft') {
      onSelect(previousSegment(selectionStart));
    } else if (key === 'ArrowRight') {
      onSelect(nextSegment(selectionEnd));
    } else if (key === 'ArrowUp') {
      nextSegmentValue(selectionStart, -1);
    } else if (key === 'ArrowDown') {
      nextSegmentValue(selectionStart, 1);
    }
  }

  /**
   * Handles paste events into the input
   * @param event The paste event
   */
  var handlePaste = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    if (!input.current) {
      return;
    }
    var pastedText = event.clipboardData.getData('text/plain');
    log.debug('handlePaste', pastedText);

    // Normalize the pasted text
    var normalizedText = normalizePastedText(pastedText);

    // Check if the pasted value is valid
    if (isValid(normalizedText, normalizedText.length)) {
      onChange(normalizedText);
      onSelect({
        selectionStart: normalizedText.length,
        selectionEnd: normalizedText.length,
        selectionDirection: SELECTION_DIRECTION.NONE
      });
    }
  }, [input, onChange, onSelect, isValid, normalizePastedText]);
  function handleKeyDown(event) {
    if (!input.current) {
      return;
    }
    log.debug('handleKeyDown', event);
    var key = event.key;
    var _input$current3 = input.current,
      _input$current3$selec = _input$current3.selectionStart,
      selectionStart = _input$current3$selec === void 0 ? 0 : _input$current3$selec,
      _input$current3$selec2 = _input$current3.selectionEnd,
      selectionEnd = _input$current3$selec2 === void 0 ? 0 : _input$current3$selec2;
    if (selectionStart === null || selectionEnd === null) {
      log.error('Selection key event on non-text input element', event.target);
      return;
    }
    if (key === 'Enter') {
      onSubmit === null || onSubmit === void 0 || onSubmit(event);
      return;
    }
    if (key.startsWith('Arrow')) {
      handleArrowKey(event);
      return;
    }
    if (key === 'Delete' || key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();

      // Deleting at the end of the value
      if (selectionEnd >= trimTrailingMask(value, emptyMask).length) {
        var newValue = value.substring(0,
        // Delete whole selection or just the char before the cursor
        selectionStart === selectionEnd ? selectionStart - 1 : selectionStart);
        var trimmedValue = trimTrailingMask(newValue, emptyMask);
        if (trimmedValue !== value) {
          onChange(trimmedValue);
          onSelect({
            selectionStart: trimmedValue.length,
            selectionEnd: trimmedValue.length,
            selectionDirection: SELECTION_DIRECTION.NONE
          });
        }
        return;
      }
      if (selectionStart !== selectionEnd) {
        // Replace all non-masked characters with blanks, set selection to start
        var _newValue = value.substring(0, selectionStart) + value.substring(selectionStart, selectionEnd).replace(/[a-zA-Z0-9]/g, FIXED_WIDTH_SPACE) + value.substring(selectionEnd);
        log.debug('Range ', selectionStart, selectionEnd, 'deleted, setting value', _newValue);
        onChange(_newValue);
        onSelect({
          selectionStart,
          selectionEnd: selectionStart,
          selectionDirection: SELECTION_DIRECTION.NONE
        });
      } else if (selectionStart > 0) {
        for (var i = selectionStart - 1; i >= 0; i -= 1) {
          // Only replace non placeholder text
          var _newValue2 = value.substring(0, i) + value.substring(i, selectionStart).replace(/[a-zA-Z0-9]/g, FIXED_WIDTH_SPACE) + value.substring(selectionStart);
          if (_newValue2 !== value) {
            onChange(_newValue2);
            onSelect({
              selectionStart: i,
              selectionEnd: i,
              selectionDirection: SELECTION_DIRECTION.NONE
            });
            return;
          }
        }
      }
      return;
    }
    if (event.altKey || event.metaKey || event.ctrlKey || key.length > 1) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    // Get the different permutations of the character they entered, remove duplicates
    var newChars = Array.from(new Set([key, key.toUpperCase(), key.toLowerCase()]));
    for (var _i2 = 0; _i2 < newChars.length; _i2 += 1) {
      var newChar = newChars[_i2];

      // If they're typing an alphanumeric character, be smart and allow it to jump ahead
      var maxReplaceIndex = /[a-zA-Z0-9]/g.test(newChar) ? examples[0].length - 1 : selectionStart;
      for (var replaceIndex = selectionStart; replaceIndex <= maxReplaceIndex; replaceIndex += 1) {
        // Fill with the example chars if necessary
        var filledValue = fillToLength(value, examples[0], replaceIndex + 1);
        var _newValue3 = getPreferredReplacementString(filledValue, replaceIndex, newChar, selectionStart, selectionEnd);
        if (isValid(_newValue3, replaceIndex + 1)) {
          var currentSegment = getSegment(replaceIndex);
          var newSelectionStart = replaceIndex + 1;
          var newSelection = {
            selectionStart: newSelectionStart,
            selectionEnd: newSelectionStart,
            selectionDirection: SELECTION_DIRECTION.NONE
          };
          if (newSelectionStart >= currentSegment.selectionEnd) {
            var nextSegmentSelection = nextSegment(replaceIndex);
            if (nextSegmentSelection.selectionStart !== currentSegment.selectionStart) {
              newSelection = nextSegmentSelection;
            }
          }
          log.debug('handleKeyDown', key, '=>', _newValue3, newSelection);
          onChange(_newValue3);
          onSelect(newSelection);
          return;
        }
      }
    }
  }

  // Need to use "text" type so we can apply a pattern and make selection properly
  return /*#__PURE__*/_jsx("input", {
    ref: input,
    className: classNames('form-control masked-input', className),
    type: "text",
    pattern: pattern,
    placeholder: placeholder,
    value: value,
    onChange: () => undefined,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
    onSelect: handleSelect,
    onSelectCapture: handleSelectCapture,
    onFocus: onFocus,
    onBlur: onBlur,
    "data-testid": dataTestId
  });
});
export default MaskedInput;
//# sourceMappingURL=MaskedInput.js.map